import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, day } from '../../lib'
import { ErrorBox, Loading } from '../../app/ui'
import type { Source } from './common'

type Connect = { connected: boolean; missing_permissions: string[]; stripe_account_id: string; livemode: boolean; connection_id?: string }

export function StripeCard({ pid, canEdit, onDirtyChange }: { pid: string; canEdit: boolean; onDirtyChange: (dirty: boolean) => void }) {
  const qc = useQueryClient()
  const src = useQuery({
    queryKey: ['ingest', 'sources', pid],
    queryFn: () => api<{ sources: Source[]; webhook_events: string[] }>('GET', `/api/partners/${pid}/sources`),
    refetchInterval: 15_000,
  })
  const [key, setKey] = useState('')
  const [secrets, setSecrets] = useState<Record<string, string>>({})
  const dirty = Boolean(key) || Object.values(secrets).some(Boolean)
  useEffect(() => { onDirtyChange(dirty) }, [dirty, onDirtyChange])
  const connect = useMutation({
    mutationFn: () => api<Connect>('POST', `/api/partners/${pid}/stripe-connections`, { restricted_key: key }),
    onSuccess: (r) => {
      if (r.connected) setKey('')
      qc.invalidateQueries({ queryKey: ['ingest'] })
    },
  })
  if (!src.data) return src.error ? <ErrorBox error={src.error} /> : <Loading />
  const conns = src.data.sources.filter((s) => s.kind === 'stripe_key' && s.connection_id)
  return (
    <div className="stack">
      <section className="stack">
        <p className="muted">Connect once.</p>
        {canEdit ? (
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault()
              connect.mutate()
            }}
          >
            <input
              data-testid="ingest-stripe-key"
              type="password"
              autoComplete="off"
              placeholder="rk_live_…"
              value={key}
              disabled={connect.isPending}
              onChange={(e) => setKey(e.target.value)}
              aria-label="Restricted key"
            />
            <button type="submit" data-testid="ingest-stripe-connect" disabled={!key || connect.isPending}
              title={key ? undefined : 'Paste a restricted key first'}>
              Connect
            </button>
          </form>
        ) : (
          <p className="muted">Only partner owners and admins can connect Stripe.</p>
        )}
        {connect.error && <ErrorBox error={connect.error} />}
        {connect.data && !connect.data.connected && (
          <div className="error-box" role="alert" data-testid="ingest-stripe-missing">
            This key cannot read {connect.data.missing_permissions.join(', ')}. Add Read for those and paste it again.
          </div>
        )}
        {connect.data?.connected && <p data-testid="ingest-stripe-connected">Connected.</p>}
      </section>

      <section className="stack">
        {conns.map((c) => <Connection key={c.id} c={c} canEdit={canEdit} events={src.data.webhook_events} secret={secrets[c.id] ?? ''}
          setSecret={(value) => setSecrets((s) => ({ ...s, [c.id]: value }))} />)}
      </section>
    </div>
  )
}

function Connection({ c, canEdit, events, secret, setSecret }: {
  c: Source; canEdit: boolean; events: string[]; secret: string; setSecret: (value: string) => void
}) {
  const qc = useQueryClient()
  const save = useMutation({
    mutationFn: () => api<{ webhook_url: string }>('POST', `/api/stripe-connections/${c.connection_id}/webhook-secret`, { secret }),
    onSuccess: () => {
      setSecret('')
      qc.invalidateQueries({ queryKey: ['ingest'] })
    },
  })
  const hookUrl = c.webhook_url ?? `${window.location.origin}/hooks/stripe/${c.connection_id}`
  return (
    <div className="stack" data-testid="ingest-stripe-connection">
      <p>
        <strong>{c.stripe_account_id}</strong> {c.livemode ? '' : <span className="tag">test mode</span>}{' '}
        <span className={c.status === 'error' ? 'negative' : 'muted'}>{c.status === 'error' ? 'Not working' : c.status === 'active' ? 'Connected' : 'Setting up'}</span>
      </p>
      <p>
        Last update: <span data-testid="ingest-stripe-last-event">{c.last_event_at ? day(c.last_event_at) : 'none yet'}</span>
      </p>
      {c.last_error && <div className="error-box">{c.last_error}</div>}
      <details>
        <summary>Faster updates (optional)</summary>
        <p className="muted">
          Add a webhook in Stripe at <code data-testid="ingest-webhook-url">{hookUrl}</code> with these events, then paste its signing secret:{' '}
          <code>{events.join(', ')}</code>
        </p>
        {c.webhook_on && <p>On.</p>}
        {canEdit && (
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate()
            }}
          >
            <input type="password" autoComplete="off" placeholder="whsec_…" value={secret} onChange={(e) => setSecret(e.target.value)}
              aria-label="Signing secret" data-testid="ingest-webhook-secret" disabled={save.isPending} />
            <button type="submit" disabled={!secret || save.isPending}>Save secret</button>
          </form>
        )}
        {save.error && <ErrorBox error={save.error} />}
      </details>
    </div>
  )
}
